import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout, Menu, Button, Table, Select, Input, Space, message, Card, Tag, Typography, Dropdown, Tooltip, Modal, Form, Row, Col, Checkbox, Drawer } from 'antd';
import { 
  PlusOutlined, 
  ReloadOutlined, 
  LogoutOutlined, 
  DownOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  KeyOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';

const { Header, Content } = Layout;
const { Option } = Select;
const { Text } = Typography;

// 主题颜色
const THEME = {
  primary: '#1890ff',
  lightBlue: '#e6f7ff',
  darkBlue: '#096dd9',
  white: '#ffffff',
  lightGrey: '#f0f2f5',
  grey: '#d9d9d9',
  textPrimary: '#000000d9',
  textSecondary: '#00000073',
  green: '#52c41a',
  red: '#ff4d4f'
};

// AWS区域列表
const awsRegions = [
  { value: 'us-east-1', label: '美国(弗吉尼亚北部)-us-east-1' },
  { value: 'us-east-2', label: '美国(俄亥俄州)-us-east-2' },
  { value: 'us-west-1', label: '美国(加利福尼亚北部)-us-west-1' },
  { value: 'us-west-2', label: '美国(俄勒冈州)-us-west-2' },
  { value: 'ca-central-1', label: '加拿大(中部)-ca-central-1' },
  { value: 'eu-west-3', label: '欧洲(巴黎)-eu-west-3' },
  { value: 'eu-south-1', label: '欧洲(意大利米兰)-eu-south-1' },
  { value: 'eu-south-2', label: '欧洲(西班牙阿拉贡)-eu-south-2' },
  { value: 'eu-central-1', label: '欧洲(法兰克福)-eu-central-1' },
  { value: 'eu-west-2', label: '欧洲(伦敦)-eu-west-2' },
  { value: 'eu-north-1', label: '欧洲(斯德哥尔摩)-eu-north-1' },
  { value: 'ap-east-1', label: '亚太地区(香港)-ap-east-1' },
  { value: 'ap-northeast-1', label: '亚太地区(东京)-ap-northeast-1' },
  { value: 'ap-northeast-2', label: '亚太地区(首尔)-ap-northeast-2' },
  { value: 'ap-southeast-1', label: '亚太地区(新加坡)-ap-southeast-1' },
  { value: 'ap-southeast-2', label: '亚太地区(悉尼)-ap-southeast-2' }
];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('us-east-1');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [keyLoading, setKeyLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [keyModalVisible, setKeyModalVisible] = useState(false);
  
  // 实例操作相关状态
  const [terminatingInstances, setTerminatingInstances] = useState({});
  const [creatingSOCKS5Instances, setCreatingSOCKS5Instances] = useState({});
  const [changingIPInstances, setChangingIPInstances] = useState({});
  
  // 实例刷新相关状态
  const [lastUpdated, setLastUpdated] = useState(null);
  const [instanceFilterVisible, setInstanceFilterVisible] = useState(false);
  const [selectedRegionsFilter, setSelectedRegionsFilter] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  
  // 授权AMI相关状态
  const [amiModalVisible, setAmiModalVisible] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [amiLoading, setAmiLoading] = useState(false);
  
  // 弹性IP相关状态
  const [elasticIPs, setElasticIPs] = useState([]);
  const [showElasticIPs, setShowElasticIPs] = useState(false);
  const [elasticIPLoading, setElasticIPLoading] = useState(false);
  const [releasingIPs, setReleasingIPs] = useState({});
  const [releaseAllIPsLoading, setReleaseAllIPsLoading] = useState(false);
  
  // 缓存最后一次的实例数据
  useEffect(() => {
    const cachedInstances = localStorage.getItem('cached_instances');
    if (cachedInstances) {
      try {
        setInstances(JSON.parse(cachedInstances));
      } catch (e) {
        console.error('解析缓存实例数据失败:', e);
      }
    }
  }, []);
  
  // 增量加载实例列表
  const fetchInstancesIncremental = useCallback(async () => {
    if (!user || !user.email) return;
    
    // 重置刷新进度
    setRefreshProgress(0);
    setInstancesLoading(true);
    
    try {
      // 简化请求体，移除区域筛选功能
      const requestBody = {
        email: user.email
      };
      
      // 初始请求 - 快速获取常用区域
      const response = await axios.post('https://aws-ads.top/api/incremental-instances', requestBody);
      
      if (response.data.success) {
        // 设置第一批数据（常用区域）
        const firstBatchData = response.data.data || [];
        // 将新数据与现有数据合并，避免重复
        setInstances(prevInstances => {
          const newInstances = [...prevInstances];
          firstBatchData.forEach(newInstance => {
            const existingIndex = newInstances.findIndex(
              i => i.instanceId === newInstance.instanceId && i.region === newInstance.region
            );
            if (existingIndex >= 0) {
              newInstances[existingIndex] = newInstance;
            } else {
              newInstances.push(newInstance);
            }
          });
          return newInstances;
        });
        
        // 更新进度到50%
        setRefreshProgress(50);
        
        // 如果有长尾数据，继续等待
        if (response.data.pendingRegions && response.data.pendingRegions.length > 0) {
          // 轮询检查剩余区域的数据
          let attempts = 0;
          const maxAttempts = 10; // 最多轮询10次
          
          const pollRemainingData = async () => {
            if (attempts >= maxAttempts) {
              console.log('已达到最大轮询次数，停止轮询');
              setRefreshProgress(100);
              return;
            }
            
            attempts++;
            
            try {
             const pollResponse = await axios.post('https://aws-ads.top/api/remaining-instances', {
              email: user.email,
              requestId: response.data.requestId
            });
              
              if (pollResponse.data.success) {
                // 增量更新UI
                const additionalData = pollResponse.data.data || [];
                
                // 更新实例列表
                setInstances(prevInstances => {
                  const newInstances = [...prevInstances];
                  additionalData.forEach(newInstance => {
                    const existingIndex = newInstances.findIndex(
                      i => i.instanceId === newInstance.instanceId && i.region === newInstance.region
                    );
                    if (existingIndex >= 0) {
                      newInstances[existingIndex] = newInstance;
                    } else {
                      newInstances.push(newInstance);
                    }
                  });
                  return newInstances;
                });
                
                // 计算进度
                const completionPercentage = 50 + (50 * (additionalData.length / (response.data.pendingRegions.length * 5)));
                setRefreshProgress(Math.min(100, Math.round(completionPercentage)));
                
                // 如果全部完成或没有更多数据
                if (pollResponse.data.completed || !pollResponse.data.hasMoreData) {
                  setRefreshProgress(100);
                } else if (attempts < maxAttempts) {
                  // 继续轮询
                  setTimeout(pollRemainingData, 3000);
                }
              }
            } catch (error) {
              console.error('轮询剩余实例数据失败:', error);
              // 即使失败也更新进度
              setRefreshProgress(prev => Math.min(100, prev + 10));
              
              if (attempts < maxAttempts) {
                setTimeout(pollRemainingData, 3000);
              }
            }
          };
          
          // 开始轮询
          setTimeout(pollRemainingData, 2000);
        } else {
          setRefreshProgress(100);
        }
        
        // 更新最后刷新时间
        const now = new Date();
        setLastUpdated(now);
        localStorage.setItem('last_instances_update', now.toISOString());
        
        // 缓存数据
        localStorage.setItem('cached_instances', JSON.stringify(
          response.data.data || []
        ));
      } else {
        message.error(response.data.message || '获取实例列表失败');
        setRefreshProgress(100);
      }
    } catch (error) {
      if (error.message === '请求超时') {
        message.error('获取实例列表超时，请检查网络连接');
      } else {
        message.error('获取实例列表失败: ' + (error.response?.data?.message || error.message));
      }
      setRefreshProgress(100);
    } finally {
      setInstancesLoading(false);
    }
  }, [user]);
  
  // 兼容原有的fetchInstances，但使用增量加载
  const fetchInstances = useCallback(async () => {
    fetchInstancesIncremental();
  }, [fetchInstancesIncremental]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  // 添加定时刷新功能
  useEffect(() => {
    // 仅当有实例时才启动自动刷新
    if (instances.length > 0) {
      // 创建自动刷新的定时器
      const intervalId = setInterval(() => {
        // 检查如果有正在进行中的操作，则不自动刷新
        const hasOngoingOperations = 
          Object.values(terminatingInstances).some(status => status) ||
          Object.values(creatingSOCKS5Instances).some(status => status) ||
          Object.values(changingIPInstances).some(status => status);
        
        if (!hasOngoingOperations && !instancesLoading) {
          console.log('自动刷新实例状态...');
          fetchInstances();
        }
      }, 15000); // 每15秒刷新一次
      
      // 清除定时器
      return () => clearInterval(intervalId);
    }
  }, [instances, fetchInstances, instancesLoading, terminatingInstances, creatingSOCKS5Instances, changingIPInstances]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSetKey = async () => {
    if (!accessKeyId || !secretAccessKey) {
      message.error('请填写完整的 AWS 密钥信息');
      return;
    }

    setKeyLoading(true);
    try {
      const response = await api.setKey(user.email, accessKeyId, secretAccessKey);
      if (response.data.success) {
        message.success('AWS 访问密钥设置成功');
        setKeyModalVisible(false);
      } else {
        message.error(response.data.message || 'AWS 访问密钥设置失败');
      }
    } catch (error) {
      message.error('AWS 访问密钥设置失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setKeyLoading(false);
    }
  };

  const handleCreateInstance = async () => {
    setCreateLoading(true);
    try {
      const response = await api.createInstance(selectedRegion, user.email);
      if (response.data.success) {
        message.success('EC2 实例创建成功');
        fetchInstances();
      } else {
        message.error(response.data.message || '创建实例失败');
      }
    } catch (error) {
      message.error('创建实例失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleTerminateInstance = async (instanceId, region) => {
    // 只设置当前操作实例的loading状态
    setTerminatingInstances(prev => ({ ...prev, [instanceId]: true }));
    
    try {
      const response = await api.terminateInstance(instanceId, region, user.email);
      if (response.data.success) {
        message.success('实例终止成功');
        // 立即更新当前实例状态为terminating
        setInstances(prevInstances => 
          prevInstances.map(instance => 
            instance.instanceId === instanceId 
              ? { ...instance, state: 'terminating' } 
              : instance
          )
        );
        
        // 然后在后台刷新所有实例以获取最新状态
        fetchInstances();
      } else {
        message.error(response.data.message || '实例终止失败');
      }
    } catch (error) {
      message.error('实例终止失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setTerminatingInstances(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const handleCreateSocks5 = async (instanceId, region) => {
    // 只设置当前操作实例的loading状态
    setCreatingSOCKS5Instances(prev => ({ ...prev, [instanceId]: true }));
    
    try {
      const response = await api.createSocks5(instanceId, region, user.email);
      if (response.data.success) {
        message.success('Socks5 代理创建成功');
        // 立即更新当前实例的Socks5状态
        setInstances(prevInstances => 
          prevInstances.map(instance => 
            instance.instanceId === instanceId 
              ? { ...instance, socks5State: 'running' } 
              : instance
          )
        );
        
        // 然后在后台刷新所有实例
        fetchInstances();
      } else {
        message.error(response.data.message || 'Socks5 代理创建失败');
      }
    } catch (error) {
      message.error('Socks5 代理创建失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreatingSOCKS5Instances(prev => ({ ...prev, [instanceId]: false }));
    }
  };

  const handleChangeIP = async (instanceId, region) => {
    // 只设置当前操作实例的loading状态
    setChangingIPInstances(prev => ({ ...prev, [instanceId]: true }));
    
    try {
      const response = await api.changeIP(instanceId, region, user.email);
      if (response.data.success) {
        // 直接显示新IP以提高响应速度
        const newIp = response.data.ip;
        if (newIp) {
          setInstances(prevInstances => 
            prevInstances.map(instance => 
              instance.instanceId === instanceId 
                ? { ...instance, publicIpAddress: newIp } 
                : instance
            )
          );
          message.success(`IP 更换成功: ${newIp}`);
        } else {
          message.success('IP 更换成功');
        }
        
        // 然后在后台刷新所有实例
        fetchInstances();
      } else {
        message.error(response.data.message || 'IP 更换失败');
      }
    } catch (error) {
      message.error('IP 更换失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setChangingIPInstances(prev => ({ ...prev, [instanceId]: false }));
    }
  };
  
  // 处理授权AMI
  const handleAuthorizeAMI = async () => {
    if (!accountId) {
      message.error('请输入AWS账户ID');
      return;
    }
    
    setAmiLoading(true);
    try {
      const response = await api.authorizeAmiToAccount(accountId, user.email);
      if (response.data.success) {
        message.success('AMI授权成功');
        setAmiModalVisible(false);
        setAccountId('');
      } else {
        message.error(response.data.message || 'AMI授权失败');
      }
    } catch (error) {
      message.error('AMI授权失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setAmiLoading(false);
    }
  };
  
  // 获取弹性IP列表 - 修复函数调用
  const fetchElasticIPs = async () => {
    setElasticIPLoading(true);
    try {
      // 设置请求超时，修复getElasticIPs调用
      const response = await Promise.race([
        api.getElasticIPs(user.email),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('请求超时')), 30000)
        )
      ]);
      
      if (response.data.success) {
         setElasticIPs((response.data.data || []).map(item => ({
         publicIp: item.PublicIp || '',
         allocationId: item.AllocationId || '',
         region: item.region || '',
         instanceId: item.InstanceId || '',
        })));

      } else {
        message.error(response.data.message || '获取弹性IP失败');
        // 如果已有数据，保留现有数据
        if (elasticIPs.length === 0) {
          setElasticIPs([]);
        }
      }
    } catch (error) {
      if (error.message === '请求超时') {
        message.error('获取弹性IP超时，请检查网络连接');
      } else {
        message.error('获取弹性IP失败: ' + (error.response?.data?.message || error.message));
      }
      // 保留现有数据
    } finally {
      setElasticIPLoading(false);
    }
  };
  
  // 释放弹性IP - 优化版本
  const handleReleaseIP = async (allocationId, region) => {
    // 设置单个IP的释放状态
    setReleasingIPs(prev => ({ ...prev, [allocationId]: true }));
    
    try {
      const response = await api.releaseIP(allocationId, region, user.email);
      if (response.data.success) {
        message.success('释放弹性IP成功');
        // 立即从UI中移除该IP
        setElasticIPs(prev => prev.filter(ip => ip.allocationId !== allocationId));
        // 后台刷新完整列表
        fetchElasticIPs();
      } else {
        message.error(response.data.message || '释放弹性IP失败');
      }
    } catch (error) {
      message.error('释放弹性IP失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setReleasingIPs(prev => ({ ...prev, [allocationId]: false }));
    }
  };

  // 释放所有弹性IP
  const handleReleaseAllIPs = async () => {
    Modal.confirm({
      title: '确认操作',
      icon: <ExclamationCircleOutlined />,
      content: '您确定要释放所有弹性IP吗？此操作不可恢复。',
      okText: '确认释放',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setReleaseAllIPsLoading(true);
        try {
          const response = await api.releaseAllIPs(user.email);
          if (response.data.success) {
            message.success(response.data.message || '所有弹性IP已成功释放');
            fetchElasticIPs();
          } else {
            message.error(response.data.message || '释放所有弹性IP失败');
          }
        } catch (error) {
          message.error('释放所有弹性IP失败: ' + (error.response?.data?.message || error.message));
        } finally {
          setReleaseAllIPsLoading(false);
        }
      },
    });
  };
  
  // 切换弹性IP显示
  const toggleElasticIPs = () => {
    const newState = !showElasticIPs;
    setShowElasticIPs(newState);
    
    if (newState) {
      fetchElasticIPs();
    }
  };

  // 自动刷新弹性IP列表
  useEffect(() => {
    if (showElasticIPs) {
      const intervalId = setInterval(() => {
        if (!elasticIPLoading && 
            !Object.values(releasingIPs).some(status => status) &&
            !releaseAllIPsLoading) {
          fetchElasticIPs();
        }
      }, 20000); // 每20秒刷新一次
      
      return () => clearInterval(intervalId);
    }
  }, [showElasticIPs, elasticIPLoading, releasingIPs, releaseAllIPsLoading]);

  // 复制IP地址到剪贴板
  const handleCopyIP = (ip) => {
    navigator.clipboard.writeText(ip)
      .then(() => {
        message.success('IP地址已复制到剪贴板');
      })
      .catch(err => {
        // 如果navigator.clipboard不可用，使用备用方法
        try {
          const textarea = document.createElement('textarea');
          textarea.value = ip;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          message.success('IP地址已复制到剪贴板');
        } catch (fallbackErr) {
          message.error('复制失败，请手动复制');
          console.error('复制失败:', err, fallbackErr);
        }
      });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'instanceId',
      key: 'instanceId',
      width: 180,
      ellipsis: true,
    },
    {
      title: '区域',
      dataIndex: 'region',
      key: 'region',
      width: 220,
      ellipsis: true,
      render: (region) => {
        const regionObj = awsRegions.find(r => r.value === region);
        return regionObj ? regionObj.label : region;
      }
    },
    {
      title: 'IP地址',
      dataIndex: 'publicIpAddress',
      key: 'publicIpAddress',
      width: 160,
      render: (ip) => (
        <Space>
          <span>{ip}</span>
          {ip && (
            <Button 
              type="text" 
              size="small" 
              icon={<CopyOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleCopyIP(ip);
              }}
            />
          )}
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => (
        <Tag color={state === 'running' ? THEME.green : state === 'pending' ? 'gold' : 'default'}>
          {state === 'running' ? '运行中' : state === 'pending' ? '创建中' : state}
        </Tag>
      )
    },
    {
      title: 'Socks5状态',
      dataIndex: 'socks5State',
      key: 'socks5State',
      width: 120,
      render: (state) => (
        <Tag color={state === 'running' ? THEME.green : state === 'pending' ? 'gold' : 'default'}>
          {state === 'running' ? '运行中' : state === 'pending' ? '创建中' : '未创建'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button 
            size="small" 
            onClick={() => handleCreateSocks5(record.instanceId, record.region)}
            disabled={record.socks5State === 'running' || record.state !== 'running'}
            loading={creatingSOCKS5Instances[record.instanceId]}
            type="primary"
            ghost
          >
            配置Socks5
          </Button>
          <Button 
            size="small" 
            onClick={() => handleChangeIP(record.instanceId, record.region)}
            disabled={record.state !== 'running'}
            loading={changingIPInstances[record.instanceId]}
            type="primary"
            ghost
          >
            更换IP
          </Button>
          <Button 
            size="small" 
            danger
            onClick={() => handleTerminateInstance(record.instanceId, record.region)}
            loading={terminatingInstances[record.instanceId]}
          >
            销毁
          </Button>
        </Space>
      ),
    },
  ];
  
  // 弹性IP表格列定义 - 增加复制功能和加载状态
  const elasticIPColumns = [
    {
      title: 'IP地址',
      dataIndex: 'publicIp',
      key: 'publicIp',
      width: 160,
      render: (ip) => (
        <Space>
          <span>{ip}</span>
          {ip && (
            <Button 
              type="text" 
              size="small" 
              icon={<CopyOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleCopyIP(ip);
              }}
            />
          )}
        </Space>
      )
    },
    {
      title: '分配ID',
      dataIndex: 'allocationId',
      key: 'allocationId',
      width: 180,
      ellipsis: true,
    },
    {
      title: '区域',
      dataIndex: 'region',
      key: 'region',
      width: 200,
      render: (region) => {
        const regionObj = awsRegions.find(r => r.value === region);
        return regionObj ? regionObj.label : region;
      }
    },
    {
      title: '实例ID',
      dataIndex: 'instanceId',
      key: 'instanceId',
      width: 180,
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button 
          size="small" 
          danger
          onClick={() => handleReleaseIP(record.allocationId, record.region)}
          loading={releasingIPs[record.allocationId]}
        >
          释放
        </Button>
      ),
    },
  ];

  const userMenu = (
    <Menu>
      <Menu.Item key="logout" onClick={handleLogout} icon={<LogoutOutlined />}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: THEME.white }}>
      <Header style={{ 
        padding: '0 24px', 
        background: THEME.white, 
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: '64px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ color: THEME.green, marginRight: '8px', fontSize: '24px', fontWeight: 'bold' }}>$</span>
            <span style={{ fontSize: '20px', fontWeight: 'bold', color: THEME.textPrimary }}>aws-ads</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text style={{ marginRight: '8px' }}>地区:</Text>
            <Select 
              value={selectedRegion}
              onChange={setSelectedRegion}
              style={{ width: '280px', marginRight: '16px' }}
              dropdownMatchSelectWidth={false}
              optionLabelProp="label"
              listHeight={550}
            >
              {awsRegions.map(region => (
                <Option key={region.value} value={region.value} label={region.label}>
                  {region.label}
                </Option>
              ))}
            </Select>
            
            <Dropdown overlay={userMenu} trigger={['click']}>
              <Button type="text" style={{ marginRight: '16px', color: THEME.primary }}>
                {user?.username || 'teasoup90'} <DownOutlined />
              </Button>
            </Dropdown>
            
            <Space>
              <Button onClick={() => setAmiModalVisible(true)} type="default">授权 AMI</Button>
              <Button onClick={toggleElasticIPs} type="default">弹性 IP</Button>
              <Button type="default">AMI 未授权</Button>
            </Space>
          </div>
        </div>
      </Header>

      <Content style={{ padding: '24px 16px', maxWidth: '1400px', margin: '24px auto', width: '100%' }}>
        <Card style={{ minHeight: '400px', overflowX: 'auto' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginBottom: '20px', 
            alignItems: 'center' 
          }}>
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreateInstance}
                loading={createLoading}
              >
                创建实例
              </Button>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={fetchInstances}
                loading={instancesLoading}
              >
                刷新实例
                {refreshProgress > 0 && refreshProgress < 100 && ` (${refreshProgress}%)`}
              </Button>
              <Button 
                type="primary" 
                icon={<KeyOutlined />}
                onClick={() => setKeyModalVisible(true)}
              >
                设置 AWS 密钥
              </Button>
            </Space>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              padding: '8px 12px', 
              borderRadius: '4px',
              background: THEME.lightBlue,
              border: `1px solid ${THEME.primary}`
            }}>
              <InfoCircleOutlined style={{ color: THEME.primary, marginRight: '8px' }} />
              <Text style={{ color: THEME.darkBlue, fontSize: '15px' }}>
                socks5端口: 1080, 用户名/密码: admin/admin
                {lastUpdated && ` | 最后更新: ${lastUpdated.toLocaleTimeString()}`}
              </Text>
            </div>
          </div>
          
          <Table
            columns={columns}
            dataSource={instances}
            rowKey="instanceId"
            loading={instancesLoading}
            locale={{ 
              emptyText: (
                <div style={{ padding: '30px', textAlign: 'center' }}>
                  <p style={{ color: THEME.textSecondary, fontSize: '16px' }}>沒有数据</p>
                </div>
              ) 
            }}
            pagination={false}
            bordered
            scroll={{ x: 1100 }}
          />
        </Card>
        
        {showElasticIPs && (
          <Card 
            title="弹性 IP 列表" 
            style={{ marginTop: '24px' }} 
            extra={
              <Space>
                <Button 
                  type="primary"
                  danger 
                  onClick={handleReleaseAllIPs}
                  loading={releaseAllIPsLoading}
                >
                  释放所有IP
                </Button>
                <Button type="default" onClick={toggleElasticIPs}>
                  关闭
                </Button>
              </Space>
            }
          >
            <Table
              columns={elasticIPColumns}
              dataSource={elasticIPs}
              rowKey="allocationId"
              loading={elasticIPLoading}
              locale={{ 
                emptyText: (
                  <div style={{ padding: '30px', textAlign: 'center' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <img 
                        src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNDEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoMCAxKSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj4KICAgIDxlbGxpcHNlIGZpbGw9IiNGNUY1RjUiIGN4PSIzMiIgY3k9IjMzIiByeD0iMzIiIHJ5PSI3Ii8+CiAgICA8ZyBmaWxsLXJ1bGU9Im5vbnplcm8iIHN0cm9rZT0iI0Q5RDlEOSI+CiAgICAgIDxwYXRoIGQ9Ik01NSAxMi43Nkw0NC44NTQgMS4yNThDNDQuMzY3LjQ3NCA0My42NTYgMEgyMS4wOTNjLS43NDkgMC0xLjQ2LjQ3NC0xLjk0NyAxLjI1N0w5IDEyLjc2MVYyMmg0NnYtOS4yNHoiLz4KICAgICAgPHBhdGggZD0iTTQxLjYxMyAxNS45MzFjMC0xLjYwNS45OTQtMi45MyAyLjIyNy0yLjkzMUg1NXYxOC4xMzdDNTUgMzMuMjYgNTMuNjggMzUgNTIuMDUgMzVoLTQwLjFDMTAuMzIgMzUgOSAzMy4yNTkgOSAzMS4xMzdWMTNoMTEuMTZjMS4yMzMgMCAyLjIyNyAxLjMyMyAyLjIyNyAyLjkyOHYuMDIyYzAgMS42MDUgMS4wMDUgMi45MDEgMi4yMzcgMi45MDFoMTQuNzUyYzEuMjMyIDAgMi4yMzctMS4zMDggMi4yMzctMi45MTN2LS4wMDd6IiBmaWxsPSIjRkFGQUZBIi8+CiAgICA8L2c+CiAgPC9nPgo8L3N2Zz4K" 
                        alt="No Data" 
                        style={{ height: '60px' }}
                      />
                    </div>
                    <p style={{ color: THEME.textSecondary, fontSize: '16px' }}>暂无弹性 IP</p>
                  </div>
                )
              }}
              pagination={false}
              bordered
              scroll={{ x: 820 }}
            />
          </Card>
        )}
      </Content>
      
      {/* AWS 密钥设置模态框 */}
      <Modal
        title={<div style={{ textAlign: 'center', fontSize: '18px' }}>AWS 访问密钥设置</div>}
        visible={keyModalVisible}
        onCancel={() => setKeyModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setKeyModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={keyLoading} 
            onClick={handleSetKey}
          >
            保存
          </Button>,
        ]}
        width={500}
        centered
      >
        <Form layout="vertical">
          <Form.Item 
            label={<span style={{ fontWeight: 500 }}>Access Key ID</span>} 
            required 
            rules={[{ required: true, message: '请输入 Access Key ID'}]}
          >
            <Input 
              placeholder="AKIA..." 
              value={accessKeyId}
              onChange={(e) => setAccessKeyId(e.target.value)}
              style={{ height: '40px' }}
            />
          </Form.Item>
          <Form.Item 
            label={<span style={{ fontWeight: 500 }}>Secret Access Key</span>} 
            required
            rules={[{ required: true, message: '请输入 Secret Access Key'}]}
          >
            <Input.Password 
              placeholder="*********************" 
              value={secretAccessKey}
              onChange={(e) => setSecretAccessKey(e.target.value)}
              style={{ height: '40px' }}
            />
          </Form.Item>
          <div style={{ color: THEME.textSecondary, textAlign: 'center', marginTop: '10px' }}>
            设置AWS访问密钥后，才能使用AWS服务功能
          </div>
        </Form>
      </Modal>

      {/* 授权AMI模态框 */}
      <Modal
        title={<div style={{ textAlign: 'center', fontSize: '18px' }}>授权 AMI 到其他账户</div>}
        visible={amiModalVisible}
        onCancel={() => setAmiModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setAmiModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={amiLoading} 
            onClick={handleAuthorizeAMI}
          >
            授权
          </Button>,
        ]}
        width={500}
        centered
      >
        <Form layout="vertical">
          <Form.Item 
            label={<span style={{ fontWeight: 500 }}>AWS 账户 ID</span>} 
            required 
            name="accountId"
            rules={[{ required: true, message: '请输入AWS账户ID'}]}
          >
            <Input 
              placeholder="请输入AWS账户ID，如: 123456789012" 
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            />
          </Form.Item>
          <div style={{ color: THEME.textSecondary, textAlign: 'center', marginTop: '10px' }}>
            授权后，对方账户可以通过共享AMI启动实例
          </div>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Dashboard; 